/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
/** @packageDocumentation
 * @module Rendering
 */

import { assert } from "@itwin/core-bentley";
import { MeshEdge, OctEncodedNormalPair, PolylineIndices } from "@itwin/core-common";
import { MeshArgs, MeshArgsEdges } from "./mesh/MeshPrimitives";
import { VertexIndices } from "../../common/render/primitives/VertexIndices";
import {
  calculateEdgeTableParams, EdgeParams, IndexedEdgeParams, SegmentEdgeParams, SilhouetteParams,
} from "../../common/render/primitives/EdgeParams";
import { tesselatePolylineFromMesh, wantJointTriangles } from "./PolylineParams";
import { IModelApp } from "../../IModelApp";

function convertPolylinesAndEdges(polylines?: PolylineIndices[], edges?: MeshEdge[]): SegmentEdgeParams | undefined {
  let numIndices = undefined !== edges ? edges.length : 0;
  if (undefined !== polylines)
    for (const pd of polylines)
      numIndices += (pd.length - 1);

  if (0 === numIndices)
    return undefined;

  numIndices *= 6;
  const indexBytes = new Uint8Array(numIndices * 3);
  const endPointAndQuadIndexBytes = new Uint8Array(numIndices * 4);

  let ndx: number = 0;
  let ndx2: number = 0;

  const addPoint = (p0: number, p1: number, quadIndex: number) => {
    VertexIndices.encodeIndex(p0, indexBytes, ndx);
    ndx += 3;
    VertexIndices.encodeIndex(p1, endPointAndQuadIndexBytes, ndx2);
    endPointAndQuadIndexBytes[ndx2 + 3] = quadIndex;
    ndx2 += 4;
  };

  if (undefined !== polylines) {
    for (const pd of polylines) {
      const num = pd.length - 1;
      for (let i = 0; i < num; ++i) {
        let p0 = pd[i];
        let p1 = pd[i + 1];
        if (p1 < p0) { // swap so that lower index is first.
          p0 = p1;
          p1 = pd[i];
        }
        addPoint(p0, p1, 0);
        addPoint(p1, p0, 2);
        addPoint(p0, p1, 1);
        addPoint(p0, p1, 1);
        addPoint(p1, p0, 2);
        addPoint(p1, p0, 3);
      }
    }
  }

  if (undefined !== edges) {
    for (const meshEdge of edges) {
      const p0 = meshEdge.indices[0];
      const p1 = meshEdge.indices[1];
      addPoint(p0, p1, 0);
      addPoint(p1, p0, 2);
      addPoint(p0, p1, 1);
      addPoint(p0, p1, 1);
      addPoint(p1, p0, 2);
      addPoint(p1, p0, 3);
    }
  }

  return {
    indices: new VertexIndices(indexBytes),
    endPointAndQuadIndices: endPointAndQuadIndexBytes,
  };
}

function convertSilhouettes(edges: MeshEdge[], normalPairs: OctEncodedNormalPair[]): SilhouetteParams | undefined {
  const base = convertPolylinesAndEdges(undefined, edges);
  if (undefined === base)
    return undefined;

  const normalPairBytes = new Uint8Array(normalPairs.length * 6 * 4);
  const normalPair16 = new Uint16Array(normalPairBytes.buffer);

  let ndx = 0;
  for (const pair of normalPairs) {
    for (let i = 0; i < 6; i++) {
      normalPair16[ndx++] = pair.first.value;
      normalPair16[ndx++] = pair.second.value;
    }
  }

  return {
    indices: base.indices,
    endPointAndQuadIndices: base.endPointAndQuadIndices,
    normalPairs: normalPairBytes,
  };
}

function buildIndexedEdges(args: MeshArgsEdges, doPolylines: boolean, maxSize: number): IndexedEdgeParams | undefined {
  const hardEdges = args.edges?.edges;
  const silhouettes = args.silhouettes;
  const polylines = doPolylines ? args.polylines?.lines : undefined;

  const numHardEdges = hardEdges?.length ?? 0;
  const numSilhouettes = silhouettes?.edges?.length ?? 0;
  const numPolylines = polylines ? polylines.reduce((count: number, pd: PolylineIndices) => count + Math.max(0, pd.length - 1), 0) : 0;
  const numSegmentEdges = numHardEdges + numPolylines;
  const numTotalEdges = numSegmentEdges + numSilhouettes;
  if (numTotalEdges === 0)
    return undefined;

  // Each edge is a quad consisting of six vertices. Each vertex is an identical 24-bit index into the lookup table.
  const indices = new VertexIndices(new Uint8Array(numTotalEdges * 6 * 3));
  for (let i = 0; i < numTotalEdges; i++)
    for (let j = 0; j < 6; j++)
      indices.setNthIndex(i * 6 + j, i);

  const {width, height, silhouettePadding, silhouetteStartByteIndex } = calculateEdgeTableParams (numSegmentEdges, numSilhouettes, maxSize);

  const data = new Uint8Array(width * height * 4);
  function setUint24(byteIndex: number, value: number): void {
    data[byteIndex + 0] = value & 0x0000ff;
    data[byteIndex + 1] = (value & 0x00ff00) >>> 8;
    data[byteIndex + 2] = (value & 0xff0000) >>> 16;
  }

  function setEdge(index: number, startPointIndex: number, endPointIndex: number): void {
    const byteIndex = index * 6;
    setUint24(byteIndex, startPointIndex);
    setUint24(byteIndex + 3, endPointIndex);
  }

  let curIndex = 0;
  if (hardEdges)
    for (const edge of hardEdges)
      setEdge(curIndex++, edge.indices[0], edge.indices[1]);

  if (polylines) {
    for (const pd of polylines) {
      const num = pd.length - 1;
      for (let i = 0; i < num; i++) {
        const p0 = pd[i];
        const p1 = pd[i + 1];
        // Ensure lower index is first.
        if (p0 < p1)
          setEdge(curIndex++, p0, p1);
        else
          setEdge(curIndex++, p1, p0);
      }
    }
  }

  if (silhouettes?.edges) {
    assert(undefined !== silhouettes.normals);
    assert(silhouettes.normals.length === silhouettes.edges.length);
    function setSilhouette(index: number, start: number, end: number, normals: OctEncodedNormalPair): void {
      const byteIndex = silhouetteStartByteIndex + silhouettePadding + index * 10;
      setUint24(byteIndex, start);
      setUint24(byteIndex + 3, end);
      data[byteIndex + 6] = normals.first.value & 0xff;
      data[byteIndex + 7] = (normals.first.value & 0xff00) >>> 8;
      data[byteIndex + 8] = normals.second.value & 0xff;
      data[byteIndex + 9] = (normals.second.value & 0xff00) >>> 8;
    }

    curIndex = 0;
    for (let i = 0; i < silhouettes.edges.length; i++)
      setSilhouette(curIndex++, silhouettes.edges[i].indices[0], silhouettes.edges[i].indices[1], silhouettes.normals[i]);
  }

  return {
    indices,
    edges: {
      data,
      width,
      height,
      numSegments: numSegmentEdges,
      silhouettePadding,
    },
  };
}

/** @internal */
export function createEdgeParams(meshArgs: MeshArgs, maxWidth?: number): EdgeParams | undefined {
  const args = meshArgs.edges;
  if (!args)
    return undefined;

  const doJoints = wantJointTriangles(args.width, true === meshArgs.is2d);
  const polylines = doJoints ? tesselatePolylineFromMesh(meshArgs) : undefined;

  let segments: SegmentEdgeParams | undefined;
  let silhouettes: SilhouetteParams | undefined;
  let indexed: IndexedEdgeParams | undefined;

  if ("non-indexed" !== IModelApp.tileAdmin.edgeOptions.type) {
    indexed = buildIndexedEdges(args, !doJoints, maxWidth ?? IModelApp.renderSystem.maxTextureSize);
  } else {
    segments = convertPolylinesAndEdges(undefined, args.edges.edges);
    silhouettes = args.silhouettes.edges && args.silhouettes.normals ? convertSilhouettes(args.silhouettes.edges, args.silhouettes.normals) : undefined;
  }

  if (!segments && !silhouettes && !polylines && !indexed)
    return undefined;

  return {
    weight: args.width,
    linePixels: args.linePixels,
    segments,
    silhouettes,
    polylines,
    indexed,
  };
}
